import { SessionLimitError, type SessionReplacement } from '../../lib/sessionLimit';
import { SessionChooser } from '../../components/auth/SessionChooser';
import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
  Box,
  Card,
  Typography,
  Input,
  Button,
  FormControl,
  FormLabel,
  Alert,
  LinearProgress,
  Divider
} from '@mui/joy';
import { User as Person, Lock, Warning, UserPlus as PersonAdd, QuestionMark } from '@phosphor-icons/react';
import { authApi } from '../../lib/api';

export const LoginPage: React.FC = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [conflict, setConflict] = useState<SessionLimitError | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const nextUrl = searchParams.get('next') || '/dashboard';

  const handleSubmit = async (e?: React.FormEvent, replacement?: SessionReplacement) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await authApi.login(formData.username.trim(), formData.password, replacement);

      setConflict(null);

      // authApi.login() already stores the access token in memory via authService
      // The refresh token is stored in an httpOnly cookie by the server
      // No need to store tokens in localStorage for cookie-based auth

      // Store user info if provided (for quick access, but not required)
      if (response.user) {
        localStorage.setItem('user_info', JSON.stringify(response.user));
      }

      // Force a page reload to the dashboard to ensure AuthContext initializes properly
      // This avoids race conditions with checkAuth() during navigation
      window.location.href = nextUrl;
    } catch (err: any) {
      setConflict(err instanceof SessionLimitError ? err : null);
      setError(err.message || 'Invalid username or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  return (
    <Box
      data-joy-color-scheme="light"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: { xs: 1.5, md: 2 }
      }}
    >
      <SessionChooser
        conflict={conflict}
        loading={loading}
        onChoose={(choice) => void handleSubmit(undefined, choice)}
        onCancel={() => { setConflict(null); setError(null); }}
      />
      <Card
        sx={{
          maxWidth: 400,
          width: '100%',
          boxShadow: 'lg',
          overflow: 'hidden',
          // Login is a branded standalone screen — keep it a solid white card in
          // any app color mode (its content uses light-designed colors).
          backgroundColor: '#ffffff !important',
          backdropFilter: 'none !important',
          border: '1px solid rgba(0, 0, 0, 0.08) !important',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #7013f5 0%, #5a0fc8 100%)',
            color: 'white',
            p: { xs: 2.5, md: 3 },
            textAlign: 'center'
          }}
        >
          <Box sx={{ mb: 2 }}>
            <img 
              src="/ApexOS-Logo-White.png" 
              alt="ApexOS Logo"
              style={{ maxHeight: '80px', width: 'auto' }}
            />
          </Box>
          <Typography level="h2" sx={{ fontWeight: 300, fontSize: '1.5rem', mb: 1, color: 'white' }}>
            Welcome to ApexOS
          </Typography>
          <Typography level="body-md" sx={{ opacity: 0.75, color: 'white' }}>
            Sign in to your account
          </Typography>
        </Box>

        {/* Body */}
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          {loading && <LinearProgress sx={{ mb: 2 }} />}
          
          {error && (
            <Alert
              color="danger"
              startDecorator={<Warning />}
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Email</FormLabel>
              <Input
                size="lg"
                startDecorator={<Person />}
                placeholder="Enter your email"
                value={formData.username}
                onChange={handleInputChange('username')}
                required
                autoComplete="username"
                disabled={loading}
              />
            </FormControl>

            <FormControl sx={{ mb: 3 }}>
              <FormLabel>Password</FormLabel>
              <Input
                size="lg"
                type="password"
                startDecorator={<Lock />}
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange('password')}
                required
                autoComplete="current-password"
                disabled={loading}
              />
            </FormControl>

            <Button
              type="submit"
              fullWidth
              size="lg"
              loading={loading}
              sx={{
                background: 'linear-gradient(135deg, #7013f5 0%, #5a0fc8 100%)',
                mb: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a0fc8 0%, #4a0da8 100%)',
                }
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Link 
                to="/password-reset"
                style={{ 
                  color: '#6c757d', 
                  textDecoration: 'none',
                  fontSize: '0.875rem'
                }}
              >
                <QuestionMark size={16} style={{ marginRight: 4 }} />
                Forgot your password?
              </Link>
            </Box>
          </form>

          <Divider sx={{ my: 3 }}>or</Divider>

          <Box sx={{ textAlign: 'center' }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
              Don't have an account?
            </Typography>
            <Button
              component={Link}
              to="/signup"
              variant="outlined"
              color="primary"
              fullWidth
              startDecorator={<PersonAdd />}
              sx={{
                borderColor: '#7013f5',
                color: 'var(--brand-text, #7013f5)',
                '&:hover': {
                  background: '#7013f5',
                  color: 'white',
                }
              }}
            >
              Create Account
            </Button>
            <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 2 }}>
              Start your free trial today and get access to all features
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 2 }}>
              By signing in you agree to our{' '}
              <Link to="/terms" style={{ color: 'var(--brand-text, #7013f5)', textDecoration: 'none' }}>
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link to="/privacy" style={{ color: 'var(--brand-text, #7013f5)', textDecoration: 'none' }}>
                Privacy Policy
              </Link>
              .
            </Typography>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
